import * as Sentry from '@sentry/vue'
import {
  POST_MESSAGE_EVENTS,
  ALLOWED_ORIGINS,
} from '@/constant/postMessageEvents'

/**
 * PostMessage sender service for cross-origin communication
 * Centralized system for managing PostMessage events across different applications
 */
class PostMessageSender {
  /**
   * Send AI_IMPORT_REQUEST message to AI Chatbot
   * @param {Object} data - The data to send
   * @param {string} data.importOption - The selected import option
   * @param {Object} data.file - The uploaded file object
   * @param {string} [data.prompt] - Optional user prompt (overrides importOption as the message text)
   */
  static sendAIImportRequest(data) {
    try {
      // Validate required data
      if (!data.importOption || !data.file) {
        throw new Error('Missing required data: importOption and file are required')
      }

      const targetOrigin = ALLOWED_ORIGINS.AI_CHATBOT.getTargetOrigin()
      if (!targetOrigin) {
        throw new Error('AI Chatbot domain not configured - cannot determine target origin')
      }

      const targetWindow = ALLOWED_ORIGINS.AI_CHATBOT.getTargetWindow()
      if (!targetWindow) {
        throw new Error('AI Chatbot iframe not found or not accessible')
      }
      // Message structure matching AI Chatbot PostMessageEvent interface
      const message = {
        type: POST_MESSAGE_EVENTS.AI_IMPORT_REQUEST,
        message: data.prompt || data.importOption,
      }
      message.isImport = data.isImport
      const dataFile = data.file
      if (dataFile?.file_metadata) {
        const {
          original_filename: fileName,
          file_size: fileSize,
          content_type: contentType,
          s3_url: fileUrl,

        } = dataFile.file_metadata
        message.files = [{
          fileName, fileSize, contentType, fileUrl, hash_id: dataFile.hash_id,
        }]
      } else {
        message.files = [dataFile]
      }
      // Send the PostMessage
      targetWindow.postMessage(message, targetOrigin)
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

  /**
   * Send a text-only prompt to AI Chatbot via AI_IMPORT_REQUEST channel
   * Reuses the existing import channel — chatbot handler already supports text-only messages (no files)
   * @param {string} prompt - The prompt string to send
   */
  static sendAIPrompt(prompt) {
    try {
      if (!prompt || typeof prompt !== 'string') {
        throw new Error('Missing required data: prompt is required')
      }

      const targetOrigin = ALLOWED_ORIGINS.AI_CHATBOT.getTargetOrigin()
      if (!targetOrigin) {
        throw new Error('AI Chatbot domain not configured - cannot determine target origin')
      }

      const targetWindow = ALLOWED_ORIGINS.AI_CHATBOT.getTargetWindow()
      if (!targetWindow) {
        throw new Error('AI Chatbot iframe not found or not accessible')
      }

      const message = {
        type: POST_MESSAGE_EVENTS.AI_IMPORT_REQUEST,
        message: prompt,
        isImport: false,
      }

      targetWindow.postMessage(message, targetOrigin)
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }
}

export default PostMessageSender
